<template>
  <div class="play-builder">
    <!-- Primary Tab Bar -->
    <nav class="navbar navbar-expand primary-tab-bar w-100 mb-4" v-if="!run_play">
      <div class="container-fluid">
        <ul class="navbar-nav flex-row w-100">
          <!-- Primary Tabs Group -->
          <li class="nav-item" v-for="tab in availableTabs" :key="tab.key">
            <a href="#" class="nav-link d-flex justify-content-center primary-tab"
              :class="{ 'primary-tab-active': active_tab === tab.key }"
              @click.prevent="setTab(tab.key)"
            >
              <span>{{ tab.label }}</span>
            </a>
          </li>
          
          <div class="ms-auto d-flex align-items-center">
            <template v-if="canExport('playbooks')">
              <div v-if="hasDraftTouchpointsSelected" data-bs-toggle="tooltip" title="Draft Touchpoints cannot be previewed">
                <button 
                  class="btn btn-secondary me-3" 
                  :disabled="true">
                  <img src="/img/icons/avid-icons/rocket-02.svg" class="icon" alt="">
                  {{ selectedAssetIds.length > 0 ? `Preview ${selectedAssetIds.length} Touchpoint${selectedAssetIds.length === 1 ? '' : 's'}` : 'Preview Play' }}
                </button>
              </div>
              <button v-else
                class="btn me-3" :class="selectedAssetIds.length > 0 ? 'btn-primary' : 'btn-secondary'"
                @click="handleLaunchClick">
                <img src="/img/icons/avid-icons/rocket-02.svg" class="icon" alt="">
                {{ selectedAssetIds.length > 0 ? `Preview ${selectedAssetIds.length} Touchpoint${selectedAssetIds.length === 1 ? '' : 's'}` : 'Preview Play' }}
              </button>
            </template>
            <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
              <button class="btn btn-secondary me-3" :disabled="true">
                <img src="/img/icons/avid-icons/rocket-02.svg" class="icon" alt="">
                {{ selectedAssetIds.length > 0 ? `Preview ${selectedAssetIds.length} Touchpoint${selectedAssetIds.length === 1 ? '' : 's'}` : 'Preview Play' }}
              </button>
            </div>
          </div>
        </ul>
      </div>
    </nav>

    <!-- Content -->
    <div class="tab-content mb-5">
      <!-- Run Play View Content -->
      <div v-if="run_play">
        <play-launch-view @back-to-builder="handleBackToBuilder" :selected-assets="selectedAssetsForLaunch"></play-launch-view>
      </div>
      <!-- Inputs Tab Content -->
      <div v-else>
        <play-strategy-view ref="strategyComponent" v-show="active_tab === 'strategy'" @strategy-changed="onStrategyChanged" @strategy-saved="onStrategySaved"></play-strategy-view>
        <play-audience-view ref="audienceComponent" v-show="active_tab === 'audiences'" @audience-unsaved-changed="onAudienceUnsavedChanged" @audience-saved="onAudienceSaved"></play-audience-view>
        <touchpoints-view v-show="active_tab === 'touchpoints'" :exports="exports" :concepts="concepts" :asset-types="asset_types" :selected-asset-ids="selectedAssetIds" @cards-state-changed="updateSelectedAssets" @review-single-touchpoint="handleDeploySingle"></touchpoints-view>
      </div>
    </div>
  </div>
</template>

<script>
import { usePlaybookStore } from '../../stores/playbook';
import { useClientStore } from '../../stores/client';
import permissionsMixin from '../../mixins/permissions';
import Swal from 'sweetalert2';

export default {
  name: 'PlayBuilder',
  mixins: [permissionsMixin],
  setup() {
    const playbookStore = usePlaybookStore()
    const clientStore = useClientStore()
    return {
      playbookStore,
      clientStore
    }
  },
  data () {
    return {
      tabs: [
        { key: 'strategy', label: 'Strategy' },
        { key: 'audiences', label: 'Audiences' },
        { key: 'touchpoints', label: 'Touchpoints' },

      ],
      active_tab: 'strategy',
      run_play: false,
      isNewPlaybook: false,
      exports: [],
      concepts: [],
      selectedAssetIds: [],
      asset_types: [],
      strategyHasUnsavedChanges: false,
      audienceHasUnsavedChanges: false,
      pendingTabSwitch: null
    }
  },
  computed: {
    playbook() {
      return this.playbookStore.getSelectedPlaybook || {};
    },
    selectedAssetsForLaunch() {
      if (!Array.isArray(this.selectedAssetIds) || this.selectedAssetIds.length === 0) {
        return null
      }
      // Filter out draft touchpoints
      const assets = (this.playbookStore.getSelectedPlaybook?.assets || [])
        .filter(a => this.selectedAssetIds.includes(a.id))
        .filter(a => !this.isDraftTouchpoint(a))
      return assets.length > 0 ? assets : null
    },
    hasDraftTouchpointsSelected() {
      if (!Array.isArray(this.selectedAssetIds) || this.selectedAssetIds.length === 0) {
        return false
      }
      const assets = (this.playbookStore.getSelectedPlaybook?.assets || []).filter(a => this.selectedAssetIds.includes(a.id))
      return assets.some(a => this.isDraftTouchpoint(a))
    },
    hasSegmentByAudienceAssetTypes() {
      // Check if any playbook type asset types have segment_by_audience set to true
      const playbook = this.playbookStore.getSelectedPlaybook;
      if (!playbook?.playbook_type?.asset_types) {
        return false;
      }
      
      return playbook.playbook_type.asset_types.some(assetType => 
        assetType.pivot?.segment_by_audience == true
      );
    },
    availableTabs() {
      // Filter tabs based on whether segment_by_audience asset types exist
      return this.tabs.filter(tab => {
        if (tab.key === 'audiences') {
          return this.hasSegmentByAudienceAssetTypes;
        }
        return true;
      });
    }
  },
  beforeMount() {
    // Initialize the stores with data from window
    if (window.__CLIENT__) {
      this.clientStore.setClient(window.__CLIENT__);
    }
    if (window.__PLAYBOOK__) {
      this.playbookStore.initialize(window.__PLAYBOOK__);
    }
    
    // Initialize exports
    this.exports = window.__PLAYBOOK__?.exports || [];
    this.concepts = window.__PLAYBOOK__?.concepts || [];
    this.asset_types = window.__PLAYBOOK__?.asset_types || [];
  },
  mounted() {
    // Check if this is a new playbook (ID = 0)
    this.isNewPlaybook = this.playbook?.id === 0;
    
    // Restore selected assets from localStorage
    this.restoreSelectedAssets();
    
    // Initialize tab from URL
    this.initializeTabFromUrl();
    
    // Listen for browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState);
    
    // Listen for beforeunload to warn about unsaved changes
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    
    // Subscribe to asset updates
    this.playbookStore.subscribeToAssetUpdates(this.clientStore.getClient.id);
  },
  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    
    // Unsubscribe from asset updates
    this.playbookStore.unsubscribeFromAssetUpdates();
  },
  methods: {
    handleLaunchClick () {
      this.run_play = true
      this.pushUrl('launch')
    },
    setTab (key) {
      // Prevent switching to audiences tab if no segment_by_audience asset types exist
      if (key === 'audiences' && !this.hasSegmentByAudienceAssetTypes) {
        return
      }
      
      // Check for unsaved changes in Strategy before switching away
      if (this.active_tab === 'strategy' && key !== 'strategy' && this.hasUnsavedStrategyChanges()) {
        this.showUnsavedChangesDialog(key, 'strategy')
        return
      }
      
      // Check for unsaved changes in Audiences before switching away
      if (this.active_tab === 'audiences' && key !== 'audiences' && this.hasUnsavedAudienceChanges()) {
        this.showUnsavedChangesDialog(key, 'audiences')
        return
      }
      
      this.active_tab = key
      this.pushUrl(key)
    },
    handleBackToBuilder() {
      // Check for unsaved changes before going back
      if (this.active_tab === 'audiences' && this.hasUnsavedAudienceChanges()) {
        this.showUnsavedChangesDialog('touchpoints', 'audiences')
        return
      }
      
      this.run_play = false
      this.active_tab = 'touchpoints'
      this.pushUrl('touchpoints')
    },
    updateSelectedAssets(ids) {
      this.selectedAssetIds = Array.isArray(ids) ? ids : []
      // Persist selected assets to localStorage
      this.saveSelectedAssets()
    },
    handleDeploySingle(touchpoint) {
      this.selectedAssetIds = touchpoint && touchpoint.id ? [touchpoint.id] : []
      // Persist selected assets to localStorage
      this.saveSelectedAssets()
      this.run_play = true
      this.pushUrl('launch')
    },
    initializeTabFromUrl() {
      // First check if we have an initial tab from server-side
      if (window.__PLAYBOOK__?.initial_tab) {
        const serverTab = window.__PLAYBOOK__.initial_tab
        if ([...this.availableTabs.map(t => t.key), 'launch'].includes(serverTab)) {
          this.active_tab = serverTab
          if (serverTab === 'launch') {
            this.run_play = true
          }
          return
        }
      }
      
      // Fallback to parsing URL
      const path = window.location.pathname
      const tabMatch = path.match(/\/(strategy|audiences|touchpoints|launch)$/)
      
      if (tabMatch) {
        let tabKey = tabMatch[1]
        
        if (this.availableTabs.some(tab => tab.key === tabKey)) {
          this.active_tab = tabKey
        } else if (tabKey === 'launch') {
          this.run_play = true
        } else if (tabKey === 'audiences' && !this.hasSegmentByAudienceAssetTypes) {
          // If trying to access audiences tab but it's not available, default to strategy
          this.active_tab = 'strategy'
        }
      }
    },
    pushUrl(tabKey) {
      const currentPath = window.location.pathname
      const basePath = currentPath.replace(/\/(strategy|audiences|touchpoints|launch)$/, '')
      const newPath = `${basePath}/${tabKey}`
      
      // Update URL without triggering a page reload
      window.history.pushState({ tab: tabKey }, '', newPath)
    },
    handlePopState(event) {
      // Check for unsaved changes before navigating away from Strategy or Audiences tabs
      if (this.active_tab === 'strategy' && this.hasUnsavedStrategyChanges()) {
        // Push current state back to prevent navigation
        const currentPath = window.location.pathname
        window.history.pushState({ tab: this.active_tab }, '', currentPath)
        // Show warning dialog
        this.showUnsavedChangesDialog(null, 'strategy')
        return
      }
      
      if (this.active_tab === 'audiences' && this.hasUnsavedAudienceChanges()) {
        // Push current state back to prevent navigation
        const currentPath = window.location.pathname
        window.history.pushState({ tab: this.active_tab }, '', currentPath)
        // Show warning dialog
        this.showUnsavedChangesDialog(null, 'audiences')
        return
      }
      
      // Handle browser back/forward navigation
      if (event.state && event.state.tab) {
        if (event.state.tab === 'launch') {
          this.run_play = true
        } else {
          this.run_play = false
          this.active_tab = event.state.tab
        }
      } else {
        // Fallback to parsing URL if no state
        this.initializeTabFromUrl()
      }
    },
    handleBeforeUnload(event) {
      // Warn user if there are unsaved changes
      if (this.hasUnsavedAudienceChanges() || this.hasUnsavedStrategyChanges()) {
        event.preventDefault()
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return event.returnValue
      }
    },
    hasUnsavedStrategyChanges() {
      // Check if we're on the strategy tab and there are unsaved changes
      return this.active_tab === 'strategy' && this.strategyHasUnsavedChanges
    },
    hasUnsavedAudienceChanges() {
      // Check if we're on the audiences tab and there are unsaved changes
      return this.active_tab === 'audiences' && this.audienceHasUnsavedChanges
    },
    showUnsavedChangesDialog(targetTab, section = 'strategy') {
      const sectionName = section === 'audiences' ? 'Audiences' : 'Strategy'
      Swal.fire({
        title: 'Unsaved Changes',
        html: `
          <div>
            <p>You have unsaved changes in the ${sectionName} section. What would you like to do?</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Save Changes',
        cancelButtonText: 'Discard Changes',
        customClass: {
          popup: 'swal-warning'
        }
        }).then((result) => {
          if (result.isConfirmed) {
            // Save and continue
            if (section === 'audiences') {
              this.saveAudiencesAndContinue(targetTab)
            } else {
              this.saveStrategyAndContinue(targetTab)
            }
          } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
            // Discard changes and continue
            if (section === 'audiences') {
              this.discardAudienceChangesAndContinue(targetTab)
            } else {
              this.discardStrategyChangesAndContinue(targetTab)
            }
          }
          // If dismissed by clicking outside or pressing escape, do nothing (stay on current tab)
        })
    },
    saveStrategyAndContinue(targetTab) {
      // Get reference to the strategy component and trigger save
      const strategyComponent = this.$refs.strategyComponent
      if (strategyComponent) {
        // Store the target tab to switch to after save
        this.pendingTabSwitch = targetTab
        // Call the strategy component's save method
        strategyComponent.saveStrategy()
      } else {
        // Fallback: just switch tabs
        this.strategyHasUnsavedChanges = false
        this.active_tab = targetTab
        this.pushUrl(targetTab)
      }
    },
    discardStrategyChangesAndContinue(targetTab) {
      const strategyComponent = this.$refs.strategyComponent;
      if (strategyComponent && strategyComponent.resetUnsavedChanges) {
        strategyComponent.resetUnsavedChanges();
      }
      this.strategyHasUnsavedChanges = false;
      this.active_tab = targetTab;
      this.pushUrl(targetTab);
    },
    onStrategyChanged() {
      // Called when strategy content changes
      this.strategyHasUnsavedChanges = true
    },
    onStrategySaved() {
      // Called when strategy is saved
      this.strategyHasUnsavedChanges = false
      
      // If there's a pending tab switch, execute it now
      if (this.pendingTabSwitch) {
        const targetTab = this.pendingTabSwitch
        this.pendingTabSwitch = null
        this.active_tab = targetTab
        this.pushUrl(targetTab)
      }
    },
    onAudienceUnsavedChanged(hasUnsaved) {
      // Called when audience unsaved status changes
      this.audienceHasUnsavedChanges = hasUnsaved
    },
    onAudienceSaved() {
      // Called when audiences are saved
      this.audienceHasUnsavedChanges = false
      
      // If there's a pending tab switch, execute it now
      if (this.pendingTabSwitch) {
        const targetTab = this.pendingTabSwitch
        this.pendingTabSwitch = null
        this.active_tab = targetTab
        this.pushUrl(targetTab)
      }
    },
    saveAudiencesAndContinue(targetTab) {
      // Get reference to the audience component and trigger save
      const audienceComponent = this.$refs.audienceComponent
      if (audienceComponent) {
        // Store the target tab to switch to after save (if provided)
        if (targetTab) {
          this.pendingTabSwitch = targetTab
        }
        // Call the audience component's save method
        audienceComponent.saveAudiences()
      } else {
        // Fallback: just clear unsaved changes
        this.audienceHasUnsavedChanges = false
        if (targetTab) {
          this.active_tab = targetTab
          this.pushUrl(targetTab)
        }
      }
    },
    discardAudienceChangesAndContinue(targetTab) {
      const audienceComponent = this.$refs.audienceComponent
      if (audienceComponent && audienceComponent.resetUnsavedChanges) {
        audienceComponent.resetUnsavedChanges()
      }
      this.audienceHasUnsavedChanges = false
      if (targetTab) {
        this.active_tab = targetTab
        this.pushUrl(targetTab)
      }
    },
    isDraftTouchpoint(touchpoint) {
      // A touchpoint is in draft mode if it has no inputs or all inputs are blank
      if (!touchpoint.inputs) {
        return true
      }
      
      // Handle both object and array formats
      let inputs = []
      if (Array.isArray(touchpoint.inputs)) {
        inputs = touchpoint.inputs
      } else if (typeof touchpoint.inputs === 'object') {
        // Convert object to array of input objects
        inputs = Object.values(touchpoint.inputs)
      } else {
        return true // Invalid format, treat as draft
      }
      
      // If no inputs at all, it's draft
      if (inputs.length === 0) {
        return true
      }
      
      // Check if all inputs are blank (empty, null, undefined, or only whitespace)
      const hasNonBlankInput = inputs.some(input => {
        if (!input || typeof input !== 'object') return false
        
        // Get the value - could be input.value or just input if it's a plain value
        let value = input.value !== undefined ? input.value : null
        
        // If still no value, check if input itself is a string/number
        if (value === null && typeof input !== 'object') {
          value = input
        }
        
        if (value === null || value === undefined) return false
        
        const strValue = String(value).trim()
        
        // Exclude citations and link placeholders as they don't count as content
        const placeholder = input.placeholder || null
        if (placeholder === 'citations' || placeholder === 'link') {
          return false
        }
        
        return strValue.length > 0
      })
      
      return !hasNonBlankInput
    },
    getStorageKey() {
      // Create a unique storage key based on playbook ID
      const playbookId = this.playbook?.id || 0
      return `playbook_${playbookId}_selected_assets`
    },
    saveSelectedAssets() {
      // Save selected asset IDs to localStorage
      try {
        const storageKey = this.getStorageKey()
        localStorage.setItem(storageKey, JSON.stringify(this.selectedAssetIds))
      } catch (error) {
        console.warn('Failed to save selected assets to localStorage:', error)
      }
    },
    restoreSelectedAssets() {
      // Restore selected asset IDs from localStorage
      try {
        const storageKey = this.getStorageKey()
        const saved = localStorage.getItem(storageKey)
        if (saved) {
          const savedIds = JSON.parse(saved)
          // Validate that saved IDs are still valid (assets still exist)
          if (Array.isArray(savedIds) && savedIds.length > 0) {
            const validIds = savedIds.filter(id => {
              return this.playbook?.assets?.some(asset => asset.id === id)
            })
            if (validIds.length > 0) {
              this.selectedAssetIds = validIds
            }
          }
        }
      } catch (error) {
        console.warn('Failed to restore selected assets from localStorage:', error)
      }
    }
  }
}
</script>
